/**
 * The Quick Play roster screen (slice 11; ui-style-guide "The roster screen"). HTML over nothing:
 * the field is not up yet. Five tabs over one model (state.ts):
 *
 *   Team    generation chips, the roster strip (slice 11g: the 11 as sideways thumbnails, each
 *           tagged with its lineup slot), a search with the grade key behind `?`, a sideways row
 *           of position chips that count what the depth chart still needs (11f), the pick grid
 *           (sprite, name, types, up to four positions as pills with its grade at each, and a `›`
 *           in the corner that opens the Pokémon sheet: 11g)
 *   Lineup  (the Chart tab until 11g) the grade key, both depth charts from the slot grades
 *           ("SPEED: A"), each row's sub-line saying why and a `›` that opens the sheet for that
 *           slot; two taps swap and hold that slot (slice 11h: a small tag on the row; a pick
 *           change keeps held slots and proposes the rest); Recommend redoes every slot and
 *           clears the holds, its note counting them
 *   Moves   the roster strip picks one Pokémon (11g); its four moves as the Book's tiles, each
 *           with its football line; a tap opens the learnset; Recommended puts the drafted set back
 *   Bag     up to three bag items, each once, with what each does here
 *   Match   the AI's level (the AI picks its preset on Play, slice 10c)
 *
 * The Pokémon sheet (sheet.ts) opens from a card's `›`, a Lineup row's `›`, the Moves tab's `›` and
 * a long press on a strip thumb. The footer (who you play, the count, Play) never scrolls away. Play checks the team: problems
 * show inline above the footer and on their tabs, never in an alert. The last roster is kept on
 * the device as a convenience (store.ts).
 */
import { loadPack, type Gen } from '@tdmons/data';
import {
  DEFENSE_SLOTS,
  OFFENSE_SLOTS,
  balance,
  benchGrade,
  itemOf,
  slotGrade,
  type Creature,
  type Difficulty,
  type Grade,
  type Move,
  type Position,
  type RosterSlot,
  type Team,
  type TeamId,
  type TypeName,
} from '@tdmons/sim';
import { effectLabel } from '../game/draft';
import { itemLine, type BagEffect } from '../game/itemLine';
import { moveLine } from '../game/moveLine';
import { typeChip } from '../ui/matchupBoard';
import { revealInline, sidewaysObserver, trackSideways } from '../ui/sideways';
import { LIGHT_TYPES } from '../ui/types';
import {
  statWords,
  badgeText,
  byPosition,
  cardBadges,
  gradeKey,
  positionChips,
  positionGrades,
  slotLine,
  type LineSegment,
  type PositionGrade,
} from './positions';
import { PokemonSheet, sheetModel, type SheetContext } from './sheet';
import type { SpriteGen } from './spriteGens';
import {
  BAG_SIZE,
  MATCH_LEVELS,
  ROSTER_SIZE,
  bagItemIds,
  combatant,
  indexPack,
  isPicked,
  learnable,
  problemTab,
  problemText,
  problems,
  recommendAll,
  removeMove,
  resetMoves,
  setMove,
  slotOf,
  swap,
  toTeams,
  toggleBag,
  togglePick,
  type PackIndex,
  type OpponentId,
  type RosterState,
  type Side,
  type SwapRef,
  type Tab,
} from './state';
import { loadRoster, saveRoster, savedGens } from './store';
import { renderStrip } from './strip';

export interface RosterHandlers {
  play(teams: Record<TeamId, Team>, level: Difficulty): void;
}

const TABS: readonly Tab[] = ['team', 'chart', 'moves', 'bag', 'match'];
/** What each tab says: the Chart tab reads Lineup since slice 11g (its id stays). */
const TAB_NAME: Record<Tab, string> = {
  team: 'Team',
  chart: 'Lineup',
  moves: 'Moves',
  bag: 'Bag',
  match: 'Match',
};
const LEVEL_NAME: Record<Difficulty, string> = { rookie: 'Rookie', pro: 'Pro', allPro: 'All-Pro' };

/**
 * Slice 11h: the tag on a Lineup row whose slot a swap set, and what the grade key says it means.
 * One word, one clause: the tag means "set by you, kept until Recommend" and nothing more.
 */
const HELD_TAG = 'SET';
const HELD_CLAUSE = `${HELD_TAG} marks a slot you set by a swap; it stays until Recommend.`;
const SWAP_NOTE = 'Tap two to swap. The bench is anyone not in that chart.';

/**
 * The note beside Recommend: the swap line, and with holds how many Recommend will redo. With
 * holds the bench sentence goes, so the note keeps to two lines in the row head at 390.
 */
export function lineupNote(held: number): string {
  if (held === 0) return SWAP_NOTE;
  const which = held === 1 ? 'the one' : `the ${held}`;
  return `Tap two to swap. Recommend redoes every slot, including ${which} you set.`;
}

const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string) => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
};

function button(cls: string, text: string, onClick: () => void): HTMLButtonElement {
  const b = el('button', cls, text);
  b.type = 'button';
  b.addEventListener('click', onClick);
  return b;
}

const bst = (c: Creature) => Object.values(c.baseStats).reduce((a, b) => a + b, 0);

/** A grade in its box (slice 11f), the same on a card's pills and a chart row's grade. */
function gradeBox(grade: Grade): HTMLElement {
  const box = el('span', 'grade-box', grade);
  box.dataset.grade = grade;
  return box;
}

export class RosterScreen {
  private pack: PackIndex | null = null;
  private state: RosterState | null = null;
  private tab: Tab = 'team';
  private search = '';
  /** The first tap of a swap on the chart tab. */
  private picked: { side: Side; ref: SwapRef } | null = null;
  /** The position chip that is on, if any: the grid shows only Pokémon who list it. */
  private filter: Position | null = null;
  /** Scroll the chip that is on into view at the next render (slice 11f). */
  private reveal = false;
  /** The grade key under the Team tab's search, opened by `?`. */
  private showKey = false;
  /** The Pokémon the Moves tab shows. */
  private movesOf = 0;
  /** Problems show once Play has been pressed with some, and update live from then on. */
  private showProblems = false;
  private loading = 0;
  private readonly cards = new Map<string, HTMLButtonElement>();
  /** Each card with its `›` (slice 11g): what the grid holds, filters and orders. */
  private readonly wraps = new Map<string, HTMLElement>();
  /** Each card's listed positions with its grade at each, in Dex order (slice 11e). */
  private readonly grades = new Map<string, readonly PositionGrade[]>();
  private readonly frames = new Map<string, { gen: number; w: number; h: number }>();
  private readonly observer: IntersectionObserver | null;
  /** Re-marks the Team tab's sideways rows when their size changes (slice 11f). */
  private readonly resize = sidewaysObserver();

  private readonly gensBox: HTMLElement;
  private readonly tabs: HTMLButtonElement[];
  private readonly panels: Record<Tab, HTMLElement>;
  private readonly errors: HTMLElement;
  private readonly vs: HTMLElement;
  private readonly count: HTMLElement;
  private readonly picker: HTMLDialogElement;
  private readonly sheet: PokemonSheet;

  constructor(
    private readonly root: HTMLElement,
    private readonly offered: readonly SpriteGen[],
    private readonly handlers: RosterHandlers,
    /** A forced preset (`?opponent=`); otherwise the AI picks on Play (slice 10c). */
    private readonly force: OpponentId | null = null,
  ) {
    const part = <T extends HTMLElement>(sel: string): T => {
      const e = root.querySelector<T>(sel);
      if (!e) throw new Error(`the roster screen has no ${sel}`);
      return e;
    };
    this.gensBox = part('.roster-gens');
    this.tabs = [...root.querySelectorAll<HTMLButtonElement>('.roster-tabs [role="tab"]')];
    this.panels = Object.fromEntries(TABS.map((t) => [t, part(`#roster-${t}`)])) as Record<
      Tab,
      HTMLElement
    >;
    this.errors = part('#roster-errors');
    this.vs = part('.roster-vs');
    this.count = part('.roster-count');
    this.picker = part<HTMLDialogElement>('#move-picker');
    this.sheet = new PokemonSheet(part<HTMLDialogElement>('#mon-sheet'), (id) => this.sprite(id));
    for (const g of offered) {
      for (const [id, f] of Object.entries(g.frames)) this.frames.set(id, { gen: g.gen, ...f });
    }
    this.observer =
      typeof IntersectionObserver === 'function'
        ? new IntersectionObserver((entries) => {
            for (const e of entries) {
              if (!e.isIntersecting) continue;
              this.paintSprite(e.target as HTMLElement);
              this.observer?.unobserve(e.target);
            }
          })
        : null;

    for (const t of this.tabs) {
      t.addEventListener('click', () => this.select((t.dataset.tab as Tab | undefined) ?? 'team'));
    }
    part('#btn-play').addEventListener('click', () => this.play());
    part('#move-picker .book-close').addEventListener('click', () => this.closePicker());
    part<HTMLInputElement>('#move-search').addEventListener('input', (e) =>
      this.filterMoves((e.target as HTMLInputElement).value),
    );
  }

  /** Load the pack for the saved (or first offered) gens and draw the screen. */
  async start(): Promise<void> {
    const offered = this.offered.map((g) => g.gen);
    await this.loadGens(savedGens(offered, offered.slice(0, 1)));
  }

  show(): void {
    this.root.hidden = false;
  }

  hide(): void {
    this.closePicker();
    this.sheet.close();
    this.root.hidden = true;
  }

  /** The model, for tests and the dev console. */
  get current(): RosterState | null {
    return this.state;
  }

  private async loadGens(gens: readonly number[]): Promise<void> {
    const ticket = (this.loading += 1);
    this.renderGens(gens);
    for (const p of Object.values(this.panels)) p.replaceChildren();
    this.panels[this.tab].append(el('p', 'roster-note', `Loading Gen ${gens.join(', ')}…`));
    const pack = indexPack(await loadPack(gens as Gen[]));
    if (ticket !== this.loading) return;
    this.pack = pack;
    this.state = loadRoster(pack);
    this.buildGrid();
    this.render();
  }

  private change(next: RosterState): void {
    if (next === this.state) return;
    this.state = next;
    saveRoster(next);
    this.render();
  }

  private select(tab: Tab): void {
    this.tab = tab;
    this.reveal = true;
    this.picked = null;
    this.render();
    this.panels[tab].scrollIntoView?.({ block: 'start' });
  }

  private render(): void {
    const { state, pack } = this;
    if (!state || !pack) return;
    for (const t of this.tabs) {
      const on = t.dataset.tab === this.tab;
      t.setAttribute('aria-selected', String(on));
      t.tabIndex = on ? 0 : -1;
    }
    for (const t of TABS) this.panels[t].hidden = t !== this.tab;
    this.renderGens(state.gens);
    this.renderTeam(state);
    this.renderChart(state, pack);
    this.renderMoves(state, pack);
    this.renderBag(state);
    this.renderMatch(state);
    this.renderFoot(state, pack);
  }

  // Generations ---------------------------------------------------------------------------------

  private renderGens(gens: readonly number[]): void {
    this.gensBox.replaceChildren(
      ...this.offered.map((g) => {
        const on = gens.includes(g.gen);
        const b = button('btn gen-chip', `Gen ${g.gen}`, () => {
          const next = on ? gens.filter((x) => x !== g.gen) : [...gens, g.gen];
          if (next.length === 0) return; // one gen is always on
          if (this.state) saveRoster({ ...this.state, gens: next });
          void this.loadGens(next.sort((a, b) => a - b));
        });
        b.setAttribute('aria-pressed', String(on));
        return b;
      }),
    );
  }

  // Team: the pick grid ------------------------------------------------------------------------

  private sprite(id: string): HTMLElement {
    const box = el('span', 'roster-sprite');
    box.setAttribute('aria-hidden', 'true');
    const f = this.frames.get(id);
    if (!f) return box;
    const img = el('span', 'roster-sprite-img pixel');
    // The frame's size, for the CSS: the grid shows it whole, a thumbnail crops it (slice 11f).
    img.style.setProperty('--fw', `${f.w}px`);
    img.style.setProperty('--fh', `${f.h}px`);
    img.dataset.src = `${import.meta.env.BASE_URL}sprites/gen${f.gen}/${id}-front.png`;
    box.append(img);
    if (this.observer) this.observer.observe(img);
    else this.paintSprite(img);
    return box;
  }

  private paintSprite(img: HTMLElement): void {
    if (img.dataset.src) img.style.backgroundImage = `url("${img.dataset.src}")`;
  }

  /** The grid is built once per pack; taps only flip its cards' pressed state. */
  private buildGrid(): void {
    const pack = this.pack;
    if (!pack) return;
    this.cards.clear();
    this.wraps.clear();
    this.grades.clear();
    for (const c of pack.creatures) {
      if (!this.frames.has(c.id)) continue;
      const card = button('roster-card', '', () => {
        if (this.state && this.pack) this.change(togglePick(this.state, this.pack, c.id));
      });
      card.dataset.search = `${c.name} ${c.types.join(' ')}`.toLowerCase();
      const chips = el('span', 'roster-chips');
      chips.append(...c.types.map(typeChip));
      // Slice 11e: the positions it lists, best by grade; BST is only in the label. Slice 11f:
      // each a pill, the position beside its grade in a box. Slice 11g: four at most, then a line
      // that says how many and points at the `›` in the corner, which opens the Pokémon sheet.
      const grades = positionGrades(c);
      const shown = cardBadges(grades);
      const badges = el('span', 'roster-badges');
      for (const g of shown.shown) {
        const pill = el('span', 'roster-badge');
        pill.append(el('span', 'badge-pos', g.position), gradeBox(g.grade));
        badges.append(pill);
      }
      badges.dataset.text = badgeText(shown);
      const hint = el('span', 'roster-hint');
      hint.setAttribute('aria-hidden', 'true');
      if (shown.more > 0) {
        hint.append(el('span', '', `${grades.length} positions`), el('span', '', 'tap for all'));
      } else hint.append(el('span', '', 'tap for stats'));
      card.append(this.sprite(c.id), el('span', 'roster-name', c.name), chips, badges, hint);
      const plays = grades.map((g) => `${g.position} ${g.grade}`).join(', ');
      card.setAttribute(
        'aria-label',
        `${c.name}, ${c.types.join(' and ')}, plays ${plays}, base stat total ${bst(c)}`,
      );
      const about = button('roster-about', '›', () => this.openSheet(c.id, about));
      about.setAttribute('aria-label', `About ${c.name}`);
      const wrap = el('div', 'card-wrap');
      wrap.append(card, about);
      this.cards.set(c.id, card);
      this.wraps.set(c.id, wrap);
      this.grades.set(c.id, grades);
    }
  }

  private renderTeam(state: RosterState): void {
    const panel = this.panels.team;
    const pack = this.pack;
    if (!pack) return;
    // Slice 11f: the eleven as one sideways row of sprite thumbnails, empties numbered. Slice
    // 11g: the roster strip, in lineup order with each one's slot under it.
    const thumbs = renderStrip({
      state,
      pack,
      sprite: (id) => this.sprite(id),
      verb: (name) => `Remove ${name}`,
      onTap: (i) => {
        const p = state.picks[i];
        if (p) this.change(togglePick(state, pack, p.id));
      },
      onAbout: (i, opener) => this.openPick(i, opener),
      empties: true,
    });
    const search = el('label', 'roster-search');
    search.htmlFor = 'roster-search';
    const label = el('span', 'visually-hidden', 'Search Pokémon');
    const input = el('input');
    input.id = 'roster-search';
    input.type = 'search';
    input.placeholder = 'Search name or type';
    input.autocomplete = 'off';
    input.value = this.search;
    input.addEventListener('input', () => {
      this.search = input.value;
      this.filterGrid();
    });
    search.append(label, input);
    const help = button('btn roster-help', '?', () => {
      this.showKey = !this.showKey;
      this.render();
    });
    help.setAttribute('aria-label', 'What the grades mean');
    help.setAttribute('aria-expanded', String(this.showKey));
    help.setAttribute('aria-controls', 'roster-team-key');
    const find = el('div', 'roster-find');
    find.append(search, help);
    const key = el('p', 'roster-note roster-key', gradeKey());
    key.id = 'roster-team-key';
    key.hidden = !this.showKey;
    // Slice 11f: one sideways row of the nine positions. A position the depth chart still needs
    // carries the count of open slots (11e's `stillNeed`); a covered one dims and shows a check.
    // Both filter the grid. Advice: Play and the problems box do not read the counts.
    const chips = el('div', 'roster-filter sideways');
    chips.setAttribute('role', 'group');
    chips.setAttribute('aria-label', 'Show who plays');
    const listed = state.picks.map((p) => (this.grades.get(p.id) ?? []).map((g) => g.position));
    for (const { position: p, need } of positionChips(listed)) {
      const b = button(need > 0 ? 'btn pos-chip' : 'btn pos-chip covered', p, () =>
        this.setFilter(this.filter === p ? null : p),
      );
      b.dataset.position = p;
      b.append(need > 0 ? el('span', 'pos-need', String(need)) : el('span', 'pos-check', '✓'));
      b.setAttribute(
        'aria-label',
        `${p}, ${need > 0 ? `${need} open` : 'covered'}, show who plays ${p}`,
      );
      b.setAttribute('aria-pressed', String(this.filter === p));
      chips.append(b);
    }
    const grid = el('div', 'roster-grid');
    for (const [id, card] of this.cards) {
      const on = isPicked(state, id);
      card.setAttribute('aria-pressed', String(on));
      card.disabled = !on && state.picks.length >= ROSTER_SIZE;
      const wrap = this.wraps.get(id);
      if (wrap) grid.append(wrap);
    }
    const focused = document.activeElement === panel.querySelector('#roster-search');
    // A render rebuilds the rows: keep where each was scrolled.
    const was = ['.roster-thumbs', '.roster-filter'].map(
      (sel) => panel.querySelector(sel)?.scrollLeft ?? 0,
    );
    panel.replaceChildren(
      el(
        'h2',
        'roster-sub',
        `Your ${ROSTER_SIZE} · ${state.picks.length} picked · tap one to take it out`,
      ),
      thumbs,
      find,
      key,
      el('h2', 'roster-sub', 'Red = open slots · tap to filter'),
      chips,
      grid,
    );
    this.resize?.disconnect();
    [thumbs, chips].forEach((row, i) => {
      row.scrollLeft = was[i] ?? 0;
      trackSideways(row, this.resize);
    });
    // A chip just turned on, or on when the tab opens, is scrolled into view, however it was
    // turned on; other renders leave the row where the player put it.
    const on = chips.querySelector<HTMLElement>('[aria-pressed="true"]');
    if (this.reveal && on && !panel.hidden) revealInline(chips, on);
    this.reveal = false;
    this.filterGrid();
    if (focused) input.focus();
  }

  private setFilter(position: Position | null): void {
    this.filter = position;
    this.reveal = true;
    this.render();
  }

  /**
   * The search and the position chip together. With a chip on, only Pokémon who list that
   * position, best grade there first, then Dex order; with none, Dex order.
   */
  private filterGrid(): void {
    const q = this.search.trim().toLowerCase();
    const ids = [...this.cards.keys()];
    const listers = this.filter
      ? byPosition(
          ids.map((id) => ({ id, grades: this.grades.get(id) ?? [] })),
          this.filter,
        ).map((x) => x.id)
      : null;
    const lists = listers ? new Set(listers) : null;
    for (const [id, card] of this.cards) {
      const wrap = this.wraps.get(id);
      if (!wrap) continue;
      wrap.hidden =
        (q !== '' && !(card.dataset.search ?? '').includes(q)) ||
        (lists !== null && !lists.has(id));
    }
    const grid = this.panels.team.querySelector('.roster-grid');
    if (!grid) return;
    const order = listers ? [...listers, ...ids.filter((id) => !lists?.has(id))] : ids;
    const wraps = order.flatMap((id) => {
      const wrap = this.wraps.get(id);
      return wrap ? [wrap] : [];
    });
    if (wraps.some((wrap, i) => grid.children[i] !== wrap)) grid.replaceChildren(...wraps);
  }

  // The Pokémon sheet (slice 11g) ---------------------------------------------------------------

  private openSheet(id: string, opener: HTMLElement | null, context?: SheetContext): void {
    const { state, pack } = this;
    const creature = pack?.byId.get(id);
    if (!state || !pack || !creature) return;
    this.sheet.open(sheetModel(creature, state, pack, context), opener);
  }

  private openPick(i: number, opener: HTMLElement | null, context?: SheetContext): void {
    const id = this.state?.picks[i]?.id;
    if (id) this.openSheet(id, opener, context);
  }

  // Lineup (the Chart tab until slice 11g) ------------------------------------------------------

  private renderChart(state: RosterState, pack: PackIndex): void {
    const panel = this.panels.chart;
    const head = el('div', 'roster-row-head');
    head.append(
      button('btn btn-book', 'Recommend', () => {
        this.picked = null;
        this.change(recommendAll(state, pack));
      }),
      el('p', 'roster-note', lineupNote(state.held.offense.length + state.held.defense.length)),
    );
    const sides = el('div', 'chart-sides');
    for (const side of ['offense', 'defense'] as const)
      sides.append(this.chartSide(state, pack, side));
    panel.replaceChildren(
      head,
      el('p', 'roster-note roster-key', `${gradeKey()} ${HELD_CLAUSE}`),
      sides,
    );
  }

  private tapChart(side: Side, ref: SwapRef): void {
    const state = this.state;
    if (!state) return;
    const same = (a: SwapRef, b: SwapRef) =>
      ('slot' in a && 'slot' in b && a.slot === b.slot) ||
      ('index' in a && 'index' in b && a.index === b.index);
    if (!this.picked || this.picked.side !== side) {
      this.picked = { side, ref };
      this.render();
      return;
    }
    const first = this.picked.ref;
    this.picked = null;
    if (same(first, ref)) this.render();
    else {
      const next = swap(state, side, first, ref);
      if (next === state) this.render();
      else this.change(next);
    }
  }

  private isSelected(side: Side, ref: SwapRef): boolean {
    const p = this.picked;
    if (!p || p.side !== side) return false;
    if ('slot' in ref && 'slot' in p.ref) return p.ref.slot === ref.slot;
    if ('index' in ref && 'index' in p.ref) return p.ref.index === ref.index;
    return false;
  }

  private chartSide(state: RosterState, pack: PackIndex, side: Side): HTMLElement {
    const box = el('section', 'chart-side');
    box.setAttribute('aria-label', side === 'offense' ? 'Offense' : 'Defense');
    box.append(el('h2', 'roster-sub', side === 'offense' ? 'Offense' : 'Defense'));
    const slots: readonly RosterSlot[] = side === 'offense' ? OFFENSE_SLOTS : DEFENSE_SLOTS;
    const chart = state[side] as Partial<Record<RosterSlot, number>>;
    const other: Side = side === 'offense' ? 'defense' : 'offense';
    const held: readonly RosterSlot[] = state.held[side];
    for (const slot of slots) {
      const i = chart[slot];
      const row = button('chart-row', '', () => this.tapChart(side, { slot }));
      row.setAttribute('aria-pressed', String(this.isSelected(side, { slot })));
      const top = el('span', 'chart-top');
      const slotEl = el('span', 'chart-slot', slot);
      // Slice 11h: a slot a swap set carries the tag on its top line; the row stays its height.
      const isHeld = held.includes(slot) && i !== undefined && !!state.picks[i];
      if (isHeld) slotEl.append(' · ', el('span', 'chart-held', HELD_TAG));
      top.append(slotEl);
      if (i === undefined || !state.picks[i]) {
        top.append(el('span', 'chart-name chart-none', '— empty —'));
        row.classList.toggle('problem', this.showProblems);
        row.setAttribute('aria-label', `${slot}, empty`);
        row.append(top);
        box.append(row);
        continue;
      }
      const c = combatant(state, pack, i);
      const g = slotGrade(c, slot);
      const line = slotLine(c, slot, slotOf(state, other, i));
      top.append(el('span', 'chart-name', c.name), this.chartGrade(g.grade, statWords(g.stat)));
      row.append(top, this.chartSub(c.types), this.chartWhy(line));
      row.setAttribute(
        'aria-label',
        `${slot}, ${c.name}${isHeld ? ', set by you' : ''}, ${statWords(g.stat)}: ${g.grade}, ${line.map((x) => x.text).join('')}`,
      );
      box.append(
        this.chartLine(row, `About ${c.name} at ${slot}`, (b) =>
          this.openPick(i, b, { side, slot }),
        ),
      );
    }
    // Slice 11c: the bench as rows like the starters', each with its best grade on this side and
    // where, so the player sees why it sits. Tap two to swap works between any two rows.
    const divider = el('div', 'chart-divider');
    divider.append(el('span', 'chart-divider-label', 'Bench'));
    box.append(divider);
    state.picks.forEach((_, i) => {
      if (slotOf(state, side, i)) return;
      const c = combatant(state, pack, i);
      const g = benchGrade(c, side);
      const line = slotLine(c, g.slot, slotOf(state, other, i));
      const row = button('chart-row chart-bench-row', '', () => this.tapChart(side, { index: i }));
      row.setAttribute('aria-pressed', String(this.isSelected(side, { index: i })));
      const top = el('span', 'chart-top');
      const slot = el('span', 'chart-slot');
      slot.setAttribute('aria-hidden', 'true');
      top.append(
        slot,
        el('span', 'chart-name', c.name),
        this.chartGrade(g.grade, statWords(g.stat), ` at ${g.position}`),
      );
      row.append(top, this.chartSub(c.types), this.chartWhy(line));
      row.setAttribute(
        'aria-label',
        `${c.name}, bench, ${statWords(g.stat)}: ${g.grade} at ${g.position}, ${line.map((x) => x.text).join('')}`,
      );
      box.append(
        this.chartLine(row, `About ${c.name} at ${g.slot}`, (b) =>
          this.openPick(i, b, { side, slot: g.slot }),
        ),
      );
    });
    return box;
  }

  /** The grade in words on a row's top line, the letter in the pills' box: "SPEED: [A] AT WR". */
  private chartGrade(letter: Grade, stat: string, after = ''): HTMLElement {
    const grade = el('span', 'chart-grade', `${stat}: `);
    grade.append(gradeBox(letter), after);
    return grade;
  }

  private chartSub(types: readonly TypeName[]): HTMLElement {
    const sub = el('span', 'chart-sub');
    sub.append(...types.map(typeChip));
    return sub;
  }

  /**
   * Slice 11g: why the grade is what it is, under the chips (`slotLine`): the stat, the penalty
   * in the out-of-position red, the result and the bar. It replaces 11e's grade tap and the
   * `OUT OF POSITION` and `ALSO ON` tags.
   */
  private chartWhy(line: readonly LineSegment[]): HTMLElement {
    const why = el('span', 'chart-why-line');
    for (const seg of line) why.append(seg.out ? el('span', 'out', seg.text) : seg.text);
    return why;
  }

  /**
   * A row and its `›` (slice 11g), a button beside the row's own (a button cannot hold one): the
   * `›` opens the Pokémon sheet for that slot, anywhere else on the row is the swap.
   */
  private chartLine(
    row: HTMLButtonElement,
    label: string,
    about: (opener: HTMLElement) => void,
  ): HTMLElement {
    const line = el(
      'div',
      `chart-line${row.classList.contains('chart-bench-row') ? ' chart-bench' : ''}`,
    );
    const open = button('chart-about', '›', () => about(open));
    open.setAttribute('aria-label', label);
    line.append(row, open);
    return line;
  }

  // Moves ---------------------------------------------------------------------------------------

  private renderMoves(state: RosterState, pack: PackIndex): void {
    const panel = this.panels.moves;
    if (state.picks.length === 0) {
      panel.replaceChildren(el('p', 'roster-note', 'Pick your team first.'));
      return;
    }
    this.movesOf = Math.min(this.movesOf, state.picks.length - 1);
    const found = this.showProblems ? problems(state, pack) : [];
    // Slice 11g: the roster strip, the chosen one lit, in place of 11 name buttons.
    const was = panel.querySelector('.roster-thumbs')?.scrollLeft ?? 0;
    const strip = renderStrip({
      state,
      pack,
      sprite: (id) => this.sprite(id),
      verb: (name) => `Moves of ${name}`,
      onTap: (at) => {
        this.movesOf = at;
        this.render();
      },
      onAbout: (at, opener) => this.openPick(at, opener),
      selected: this.movesOf,
      problem: (at) =>
        found.some((x) => 'index' in x && x.index === at && problemTab(x) === 'moves'),
    });
    const i = this.movesOf;
    const pick = state.picks[i] as RosterState['picks'][number];
    const c = combatant(state, pack, i);
    const head = el('div', 'moves-head');
    const chips = el('span', 'roster-chips');
    chips.append(...c.types.map(typeChip));
    const where = [slotOf(state, 'offense', i), slotOf(state, 'defense', i)]
      .filter(Boolean)
      .join(' · ');
    const about = button('btn moves-about', '›', () => this.openPick(i, about));
    about.setAttribute('aria-label', `About ${c.name}`);
    head.append(
      el('h2', 'roster-sub moves-name', c.name),
      about,
      chips,
      el('span', 'moves-where', where || 'BENCH'),
      button('btn', pick.edited ? 'Recommended' : 'Recommended ✓', () =>
        this.change(resetMoves(state, pack, i)),
      ),
    );
    const grid = el('div', 'move-grid');
    c.moves.forEach((m, at) => grid.append(this.moveCell(m, () => this.openPicker(i, at))));
    if (c.moves.length < 4) {
      const add = button('move-tile move-add', '', () => this.openPicker(i, c.moves.length));
      add.append(el('span', 'move-name', '+ Add move'));
      const cell = el('div', 'move-cell');
      cell.append(add);
      grid.append(cell);
    }
    panel.replaceChildren(strip, head, grid);
    strip.scrollLeft = was;
    trackSideways(strip, this.resize);
  }

  private moveCell(m: Move, onClick: () => void, line = true): HTMLElement {
    const type = (m.effect.kind === 'attack' ? m.effect.type : m.type) as TypeName;
    const b = button(LIGHT_TYPES.has(type) ? 'move-tile light' : 'move-tile', '', onClick);
    b.style.background = `var(--type-${type.toLowerCase()})`;
    b.append(
      el('span', 'move-name', m.name),
      el('span', 'move-effect', effectLabel(m.effect)),
      el('span', 'move-pp', `PP ${m.pp}/${m.pp}`),
    );
    b.setAttribute(
      'aria-label',
      `${m.name}, ${type}, ${effectLabel(m.effect)}, PP ${m.pp} of ${m.pp}`,
    );
    const cell = el('div', 'move-cell');
    cell.append(b);
    if (line) cell.append(el('p', 'move-line', moveLine(m.effect)));
    return cell;
  }

  private openPicker(i: number, at: number): void {
    const { state, pack } = this;
    if (!state || !pack) return;
    const pick = state.picks[i];
    if (!pick) return;
    const name = pack.byId.get(pick.id)?.name ?? pick.id;
    const current = pick.moves[at];
    const title = this.picker.querySelector('#move-picker-title');
    if (title)
      title.textContent = current
        ? `${name}: replace ${pack.moves.get(current)?.name ?? current}`
        : `${name}: add a move`;
    const list = this.picker.querySelector('.move-options') as HTMLElement;
    const rows: HTMLElement[] = [];
    if (current && pick.moves.length > 1) {
      const li = el('li', 'move-option');
      li.append(
        button('btn move-remove', 'Remove this move', () => {
          this.change(removeMove(state, i, at));
          this.closePicker();
        }),
      );
      rows.push(li);
    }
    for (const m of learnable(state, pack, i)) {
      const li = el('li', 'move-option');
      const type = m.effect.kind === 'attack' ? m.effect.type : m.type;
      li.dataset.search = `${m.name} ${type}`.toLowerCase();
      const cell = this.moveCell(m, () => {
        if (this.state && this.pack) this.change(setMove(this.state, this.pack, i, at, m.id));
        this.closePicker();
      });
      const tile = cell.querySelector('button') as HTMLButtonElement;
      tile.setAttribute('aria-pressed', String(m.id === current));
      tile.disabled = m.id !== current && pick.moves.includes(m.id);
      li.append(cell);
      rows.push(li);
    }
    list.replaceChildren(...rows);
    const search = this.picker.querySelector<HTMLInputElement>('#move-search');
    if (search) search.value = '';
    if (!this.picker.open) this.picker.showModal?.();
    if (!this.picker.open) this.picker.setAttribute('open', '');
  }

  private filterMoves(q: string): void {
    const s = q.trim().toLowerCase();
    for (const li of this.picker.querySelectorAll<HTMLElement>('.move-option[data-search]')) {
      li.hidden = s !== '' && !(li.dataset.search ?? '').includes(s);
    }
  }

  private closePicker(): void {
    if (this.picker.open) {
      if (typeof this.picker.close === 'function') this.picker.close();
      else this.picker.removeAttribute('open');
    }
  }

  // Bag -----------------------------------------------------------------------------------------

  private renderBag(state: RosterState): void {
    const panel = this.panels.bag;
    const list = el('div', 'bag-list');
    for (const id of bagItemIds()) {
      const item = itemOf(id);
      if (!item) continue;
      const on = state.bag.includes(id);
      const name = (balance.items as unknown as Record<string, { name: string }>)[id]?.name ?? id;
      const row = el('div', 'bag-row');
      const b = button('btn bag-item', name, () => this.change(toggleBag(state, id)));
      b.setAttribute('aria-pressed', String(on));
      b.disabled = !on && state.bag.length >= BAG_SIZE;
      row.append(b, el('p', 'bag-line', itemLine(item.effect as BagEffect)));
      list.append(row);
    }
    panel.replaceChildren(
      el('h2', 'roster-sub', `Bag ${state.bag.length} / ${BAG_SIZE}`),
      el('p', 'roster-note', 'Each item once. Spent between plays on anyone on the team.'),
      list,
    );
  }

  // Match ---------------------------------------------------------------------------------------

  /**
   * The AI's level. The opponent is not chosen here: the AI picks its team once the player presses
   * Play (slice 10c), so a roster is built before the player knows what it faces (slice 10a).
   */
  private renderMatch(state: RosterState): void {
    const panel = this.panels.match;
    const levels = el('div', 'segmented');
    levels.setAttribute('role', 'group');
    levels.setAttribute('aria-label', 'AI level');
    for (const level of MATCH_LEVELS) {
      const b = button('btn', LEVEL_NAME[level], () => this.change({ ...state, level }));
      b.setAttribute('aria-pressed', String(state.level === level));
      levels.append(b);
    }
    panel.replaceChildren(
      el('h2', 'roster-sub', 'Opponent'),
      el('p', 'match-pick', 'The AI picks its team'),
      el(
        'p',
        'roster-note',
        'When you press Play it looks at your eleven and fields the preset team that matches up better. Its eleven take the field at kickoff.',
      ),
      el('h2', 'roster-sub', 'AI level'),
      levels,
    );
  }

  // Footer and Play -----------------------------------------------------------------------------

  private renderFoot(state: RosterState, pack: PackIndex): void {
    // The opponent is the AI's pick, made on Play.
    this.vs.textContent = `VS ? · ${LEVEL_NAME[state.level]}`;
    this.count.textContent = `${state.picks.length} / ${ROSTER_SIZE}`;
    const found = this.showProblems ? problems(state, pack) : [];
    const bad = new Set(found.map(problemTab));
    for (const t of this.tabs) {
      const tab = t.dataset.tab as Tab;
      t.classList.toggle('problem', bad.has(tab));
      t.textContent = `${TAB_NAME[tab]}${bad.has(tab) ? ' !' : ''}`;
    }
    this.errors.hidden = found.length === 0;
    if (found.length === 0) {
      this.errors.replaceChildren();
      return;
    }
    const list = el('ul', 'roster-errors-list');
    for (const p of found) list.append(el('li', '', problemText(p, state, pack)));
    this.errors.replaceChildren(
      el(
        'p',
        'roster-errors-head',
        `${found.length} ${found.length === 1 ? 'thing' : 'things'} to fix`,
      ),
      list,
    );
  }

  private play(): void {
    const { state, pack } = this;
    if (!state || !pack) return;
    if (problems(state, pack).length > 0) {
      this.showProblems = true;
      this.render();
      return;
    }
    this.showProblems = false;
    const teams = toTeams(state, pack, this.force);
    this.vs.textContent = `VS ${teams.B.name} · ${LEVEL_NAME[state.level]}`;
    this.handlers.play(teams, state.level);
  }
}
